import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Zap,
  Star,
  Crown,
  Database,
  Check,
  ChevronUp,
  ChevronDown,
  X,
} from "lucide-react";
import DirectAddOns from "./misc/DirectAddOns";
import FooterComponent from "../../../components/Footer";
import ContactSalesModal from "../ai/misc/ContactSalesModal";
import { useNavigate } from "react-router-dom";
import { Feature } from "../../../store/types/settings";
import AIPlanModal from "../ai/misc/AIPlanModal";
import Navbar from "../../../components/Navbar2";
import AgreementModal from "../misc/AgreementModal";
import SelectedFeaturesFloating, {
  SelectedItem,
} from "../misc/SelectedFeaturesFloating";
import LifecyclePhasesSection from "../misc/LifecyclePhasesSection";
import { featuresService } from "../../../services/api/settings";
import { offeringsService } from "../../../services/api/settings";
import { OfferingPlan, OfferingAddon } from "../../../store/types/settings";
import formatPrice from "../../../utility/formatPrice";

interface DirectPricingProps {
  billingType: "Monthly" | "Yearly";
  orgId: string;
  selectedTier?: string;
  selectedFeatureIds?: string[];
  selectedFeatures?: Feature[];
  onFeatureRemove?: (featureId: string) => void;
  provider?: string;
  onBankTransferClick?: () => void;
}

interface DirectPlan {
  name: string;
  tier: "STARTER" | "GROWTH" | "SCALE" | "ENTERPRISE";
  priceMonthly: number;
  priceYearly: number;
  setupFee: number;
  buttonText: string;
  popular: boolean;
  features: string[];
  isVip?: boolean;
}

export default function PricingDirect() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const urlTier = searchParams.get("tier");
  const [selectedTier, setSelectedTier] = useState<string | null>(
    () => urlTier,
  );
  const [billingCycle, setBillingCycle] = useState<
    "Monthly" | "Quarterly" | "Yearly"
  >(() => {
    const freq = new URLSearchParams(window.location.search).get("frequency");
    if (freq === "YEARLY") return "Yearly";
    if (freq === "QUARTERLY") return "Quarterly";
    return "Monthly";
  });
  const isYearly = billingCycle === "Yearly";
  const [aiAddOns, setAiAddOns] = useState<Feature[]>([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [selectedAIPlan, setSelectedAIPlan] = useState<{
    [key: string]: string;
  }>({});
  // Stores the integration services total (setup fee) returned from AIPlanModal per Direct plan tier
  const [aiPlanSetupFees, setAiPlanSetupFees] = useState<{
    [key: string]: number;
  }>({});
  const [directAddOnFeatures, setDirectAddOnFeatures] = useState<any[]>([]);
  const [offeringPlans, setOfferingPlans] = useState<OfferingPlan[]>([]);
  const [aiOfferingPlans, setAiOfferingPlans] = useState<OfferingPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const [lifecyclePhases, setLifecyclePhases] = useState<any[]>([]);
  const [selectedLifecyclePhaseIds, setSelectedLifecyclePhaseIds] = useState<
    number[]
  >(
    searchParams
      .get("lifecyclePhaseIds")
      ?.split(",")
      .map(Number)
      .filter((id) => !isNaN(id)) || [],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const offeringsResponse = await offeringsService.getAll();
        if (offeringsResponse.status === "success" && offeringsResponse.data) {
          setOfferingPlans([...offeringsResponse.data.DIRECT.plans].reverse());
          setAiOfferingPlans(
            [...offeringsResponse.data.RECRUITER.plans].reverse(),
          );
          // Set add-ons from offerings
          setDirectAddOnFeatures(
            [
              ...offeringsResponse.data.DIRECT.addons,
              ...offeringsResponse.data.RECRUITER.addons,
            ].reverse(),
          );
          if (offeringsResponse.data.LIFECYCLE?.phases) {
            setLifecyclePhases(offeringsResponse.data.LIFECYCLE.phases);
          }
        }
      } catch (error) {
        console.error("Failed to fetch offering data", error);
        setDirectAddOnFeatures([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  useEffect(() => {
    const fetchAiAddOns = async () => {
      try {
        if (id) {
          const response = await featuresService.getAllAvailable(id);
          if (response.status === "success" && response.data) {
            setAiAddOns(response.data);
          }
        }
      } catch (error) {
        console.error("Error fetching AI add-ons:", error);
      }
    };
    fetchAiAddOns();
  }, [id]);

  const [aiModalState, setAiModalState] = useState<{
    isOpen: boolean;
    planIndex: number | null;
  }>({
    isOpen: false,
    planIndex: null,
  });
  const [selectedDirectAddOnIds, setSelectedDirectAddOnIds] = useState<
    string[]
  >(
    searchParams
      .get("directAddOns")
      ?.split(",")
      .filter((id) => id) || [],
  );
  const [agreementModalState, setAgreementModalState] = useState<{
    isOpen: boolean;
    planTier?: string;
    planName?: string;
  }>({
    isOpen: false,
  });

  const handleDirectAddOnToggle = (id: string) => {
    setSelectedDirectAddOnIds((prev) => {
      const isSelected = prev.includes(id);
      const newIds = isSelected
        ? prev.filter((item) => item !== id)
        : [...prev, id];

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("directAddOns", newIds.join(","));
        } else {
          p.delete("directAddOns");
        }
        return p;
      });

      return newIds;
    });
  };

  const handleLifecyclePhaseToggle = (phaseId: number) => {
    setSelectedLifecyclePhaseIds((prev) => {
      const isSelected = prev.includes(phaseId);
      let newIds = isSelected
        ? prev.filter((id) => id !== phaseId)
        : [...prev, phaseId];

      const mvpPhase = lifecyclePhases.find(
        (p) => p.name && p.name.trim().toLowerCase() === "mvp foundation",
      );
      if (mvpPhase) {
        const mvpId = mvpPhase.id;
        const hasOthersSelected = newIds.some((id) => id !== mvpId);

        if (hasOthersSelected) {
          if (!newIds.includes(mvpId)) {
            newIds.push(mvpId);
          }
        } else {
          if (phaseId === mvpId && isSelected) {
            newIds = [];
          }
        }
      }

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("lifecyclePhaseIds", newIds.join(","));
        } else {
          p.delete("lifecyclePhaseIds");
        }
        return p;
      });

      return newIds;
    });
  };

  const handleAgreementConfirm = () => {
    if (!agreementModalState.planTier) return;

    // Find the plan
    const plan = plans.find((p) => p.tier === agreementModalState.planTier);
    if (!plan) return;

    const aiFeatureIds = selectedDirectAddOnIds.filter((id) =>
      aiAddOns.some((a) => a.id === id),
    );
    const directAddOnIds = selectedDirectAddOnIds.filter(
      (id) => !aiAddOns.some((a) => a.id === id),
    );

    const directAddOnsParam =
      directAddOnIds.length > 0
        ? `&directAddOns=${directAddOnIds.join(",")}`
        : "";

    const aiFeaturesParam =
      aiFeatureIds.length > 0 ? `&featureIds=${aiFeatureIds.join(",")}` : "";

    const lifecycleParam =
      selectedLifecyclePhaseIds.length > 0
        ? `&lifecyclePhaseIds=${selectedLifecyclePhaseIds.join(",")}`
        : "";

    const frequency =
      billingCycle === "Yearly"
        ? "YEARLY"
        : billingCycle === "Quarterly"
          ? "QUARTERLY"
          : "MONTHLY";
    const aiPlanTier = selectedAIPlan[plan.tier];
    const tierParam = aiPlanTier ? `&tier=${aiPlanTier}` : `&tier=${plan.tier}`;

    navigate(
      `/pricing/direct/pay?Id=${id}${tierParam}&frequency=${frequency}&directPlan=${plan.tier}&includeSetupFee=true${directAddOnsParam}${aiFeaturesParam}${lifecycleParam}`,
    );

    setAgreementModalState({ isOpen: false });
  };

  const aiPlans = aiOfferingPlans.map((plan) => ({
    name: plan.title,
    badge: plan.subtitle,
    priceMonthly: parseFloat(plan.monthlyPrice).toLocaleString(),
    priceQuarterly: plan.quarterlyPrice
      ? parseFloat(String(plan.quarterlyPrice)).toLocaleString()
      : (parseFloat(plan.monthlyPrice) * 3).toLocaleString(),
    priceYearly: parseFloat(plan.yearlyPrice).toLocaleString(),
    features: plan.points,
    integrationServices:
      (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
        ? plan
            .costBreakdown!.deploymentCosts.filter((d: any) => d && d.name)
            .map((d: any) => ({
              name: d.name,
              cost: d.price.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }))
        : plan.integrationCost
          ? [
              {
                name: "Standard Onboarding & Training",
                cost: (
                  parseFloat(plan.integrationCost || "0") || 0
                ).toLocaleString(),
              },
            ]
          : [],
    tier: plan.tier || "",
    popular: plan.tier === "ENTERPRISE",
    isVip: plan.tier === "ENTERPRISEPLUS", // Mapping ENTERPRISEPLUS to isVip like in AI page
  }));

  const handleAddAIPlan = (tier: string, setupFee: number) => {
    const directPlanTier = plans[aiModalState.planIndex!].tier;
    setSelectedAIPlan((prev) => ({
      ...prev,
      [directPlanTier]: tier,
    }));
    setAiPlanSetupFees((prev) => ({ ...prev, [directPlanTier]: setupFee }));
    setAiModalState({ isOpen: false, planIndex: null });
  };

  const handleRemoveAIPlan = (directPlanTier: string) => {
    setSelectedAIPlan((prev) => {
      const newState = { ...prev };
      delete newState[directPlanTier];
      return newState;
    });
    setAiPlanSetupFees((prev) => {
      const next = { ...prev };
      delete next[directPlanTier];
      return next;
    });
    setAiModalState({ isOpen: false, planIndex: null });
  };

  const plans = offeringPlans.map((plan) => ({
    name: plan.title,
    subtitle: plan.subtitle,
    description: plan.description,
    monthlyPrice: parseFloat(plan.monthlyPrice),
    quarterlyPrice: plan.quarterlyPrice
      ? parseFloat(String(plan.quarterlyPrice))
      : parseFloat(plan.monthlyPrice) * 3,
    yearlyPrice: parseFloat(plan.yearlyPrice),
    setupFee: (
      parseFloat(plan.setupFee || plan.integrationCost || "0") || 0
    ).toLocaleString(),
    features: plan.points,
    popular: plan.tier === "GROWTH",
    tier: plan.tier || "",
    // Handling range/primary/contactSales flags if they were used for specific styling
    isRange: plan.tier === "SCALE",
    isPrimary: plan.tier === "ENTERPRISE",
    contactSales: plan.monthlyPrice === "0" || plan.tier === "ENTERPRISE",
    integrationServices:
      (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
        ? plan
            .costBreakdown!.deploymentCosts.filter((d: any) => d && d.name)
            .map((d: any) => ({
              name: d.name,
              cost: d.price.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }))
        : plan.integrationCost
          ? [
              {
                name: "Standard Onboarding & Training",
                cost: (
                  parseFloat(plan.integrationCost || "0") || 0
                ).toLocaleString(),
              },
            ]
          : [],
  }));

  // const formatPrice = (price: number) => {
  //   return new Intl.NumberFormat("en-NG", {
  //     style: "decimal",
  //     // style: "currency",
  //     // currency: "NGN",
  //     minimumFractionDigits: 0,
  //     maximumFractionDigits: 0,
  //   }).format(price);
  // };

  const calculateTotalPrice = (plan: any): string => {
    const basePrice =
      billingCycle === "Yearly"
        ? plan.yearlyPrice
        : billingCycle === "Quarterly"
          ? plan.quarterlyPrice
          : plan.monthlyPrice;

    // Calculate add-ons total
    const addOnsTotal = selectedDirectAddOnIds.reduce((sum, id) => {
      const addon = directAddOnFeatures.find((a) => a.id === id);
      if (addon) {
        if (addon.pricing) {
          // Use numeric values directly if available
          const numericPrice =
            billingCycle === "Yearly"
              ? addon.pricing.yearlyPrice
              : billingCycle === "Quarterly"
                ? addon.pricing.quarterlyPrice || addon.pricing.monthlyPrice * 3
                : addon.pricing.monthlyPrice;

          if (typeof numericPrice === "number") {
            return sum + numericPrice;
          }

          // Fallback to parsing strings if numeric values are missing
          const pStr =
            billingCycle === "Yearly"
              ? addon.pricing.yearly
              : billingCycle === "Quarterly"
                ? addon.pricing.quarterly ||
                  (
                    parseFloat(
                      addon.pricing.monthly?.replace(/[₦,\s]/g, "") || "0",
                    ) * 3
                  ).toString()
                : addon.pricing.monthly;
          if (typeof pStr === "string") {
            return sum + parseFloat(pStr.replace(/[₦,\s]/g, "") || "0");
          }
        }

        // Fallback for purely dynamic price if pricing object is missing completely
        if (addon.price !== undefined && addon.price !== null) {
          // If it's a direct add-on, we don't divide by 1000
          // (AI add-ons are handled below in the aiAddOns find)
          return sum + (Number(addon.price) || 0);
        }
      }

      // Check if it's an AI Add-on
      const aiAddon = aiAddOns.find((a) => a.id === id);
      if (aiAddon) {
        return sum + (aiAddon.price || 0);
      }

      return sum;
    }, 0);

    // Calculate Setup Fee
    let setupFee = 0;
    if ((plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0) {
      setupFee = plan.costBreakdown!.deploymentCosts.reduce(
        (sum: number, d: any) => sum + d.price,
        0,
      );
    } else if (
      plan.setupFee &&
      typeof plan.setupFee === "string" &&
      plan.setupFee !== "Custom"
    ) {
      setupFee = parseFloat(plan.setupFee.replace(/[₦,\s]/g, "") || "0");
    }

    // Calculate Selected AI Plan Cost
    let aiPlanCost = 0;
    const selectedAiTier = selectedAIPlan[plan.tier];
    if (selectedAiTier) {
      const aiPlan = aiPlans.find((p) => p.tier === selectedAiTier);
      if (aiPlan) {
        const priceStr =
          billingCycle === "Yearly"
            ? aiPlan.priceYearly
            : billingCycle === "Quarterly"
              ? aiPlan.priceQuarterly
              : aiPlan.priceMonthly;
        if (typeof priceStr === "string") {
          aiPlanCost = parseFloat(priceStr.replace(/[₦,\s]/g, "") || "0");
        }
        // Use stored setup fee from modal instead of re-parsing strings
        aiPlanCost += aiPlanSetupFees[plan.tier] || 0;
      }
    }

    const total = basePrice + addOnsTotal + setupFee + aiPlanCost;

    return `${total.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const selectedDirectAddOns = directAddOnFeatures.filter((addon) =>
    selectedDirectAddOnIds.includes(addon.id),
  );

  const currentPlan = plans.find((p) => p.tier === selectedTier);
  const planFeatures = currentPlan
    ? currentPlan.features.map((f) => ({
        id: f,
        displayName: f,
        price: 0,
      }))
    : [];

  const allSelectedItems: SelectedItem[] = [
    ...planFeatures,
    ...selectedDirectAddOns
      .filter((a: any) => a && a.id)
      .map((a: OfferingAddon) => ({
        id: a.id,
        displayName: a.name || "Unknown Add-on",
        category: (a.serviceType === "RECRUITER" ? "AI" : "DIRECT") as
          | "AI"
          | "DIRECT",
        price:
          a.price !== undefined && a.price !== null
            ? Number(a.price)
            : (a as any).pricing
              ? billingCycle === "Yearly"
                ? (a as any).pricing.yearlyPrice ||
                  parseFloat(
                    ((a as any).pricing.yearly || "0").replace(/[₦,\s]/g, ""),
                  ) ||
                  0
                : billingCycle === "Quarterly"
                  ? (a as any).pricing.quarterlyPrice ||
                    ((a as any).pricing.monthlyPrice
                      ? (a as any).pricing.monthlyPrice * 3
                      : 0) ||
                    parseFloat(
                      ((a as any).pricing.quarterly || "0").replace(
                        /[₦,\s]/g,
                        "",
                      ),
                    ) ||
                    parseFloat(
                      ((a as any).pricing.monthly || "0").replace(
                        /[₦,\s]/g,
                        "",
                      ),
                    ) * 3 ||
                    0
                  : (a as any).pricing.monthlyPrice ||
                    parseFloat(
                      ((a as any).pricing.monthly || "0").replace(
                        /[₦,\s]/g,
                        "",
                      ),
                    ) ||
                    0
              : 0,
      })),
    ...aiAddOns
      .filter(
        (a: any) =>
          a &&
          selectedDirectAddOnIds.includes(a.id) &&
          !selectedDirectAddOns.some((s) => s.id === a.id),
      )
      .map((a: any) => ({
        id: a.id,
        displayName: a.displayName || a.name || "AI Add-on",
        category: "AI" as const,
        price: a.price || 0,
      })),
    ...lifecyclePhases
      .filter((p) => selectedLifecyclePhaseIds.includes(p.id) || p.isMandatory)
      .map((p) => ({
        id: p.id.toString(),
        displayName: p.name,
        category: "LIFECYCLE" as any,
        price:
          p.setupFee +
          p.monthlySubscription *
            (billingCycle === "Yearly"
              ? 12
              : billingCycle === "Quarterly"
                ? 3
                : 1),
      })),
  ];

  const calculateFloatingTotal = () => {
    const total = allSelectedItems.reduce(
      // (sum, item) => sum + (item.price || 0) ,
      (sum, item) =>
        sum + (item.category === "AI" ? item.price || 0 : item.price || 0),
      0,
    );
    return total.toLocaleString();
  };

  return (
    <div className="bg-white font-abel">
      <Navbar title="Login" link="/login" />
      <section className="pb-20 bg-gray-50">
        <h1 className="text-lg md:text-[30px] text-primary pt-3 leading-relaxed text-center">
          25th Direct
        </h1>
        {selectedTier && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => {
                setSelectedTier(null);
                setSearchParams((prev) => {
                  prev.delete("tier");
                  return prev;
                });
              }}
              className="px-6 py-2 bg-white border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transition-colors font-medium flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Compare all plans
            </button>
          </div>
        )}
        <div className="flex justify-center w-full pb-6 pt-3">
          <div className="bg-white rounded-full p-1.5 shadow-lg inline-flex gap-1">
            <button
              onClick={() => setBillingCycle("Monthly")}
              className={`px-8 py-3 rounded-full font-normal transition-all duration-200 relative ${
                billingCycle === "Monthly"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-700 hover:text-gray-900"
              }`}
            >
              Monthly
              <span className="absolute -top-1 right-2 px-2 py-0.5 bg-primary text-white text-[8px] rounded-full">
                + 25%
              </span>
            </button>
            <button
              onClick={() => setBillingCycle("Quarterly")}
              className={`px-8 py-3 rounded-full font-normal transition-all duration-200 relative ${
                billingCycle === "Quarterly"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-700 hover:text-gray-900"
              }`}
            >
              Quarterly
              <span className="absolute -top-1 right-2 px-2 py-0.5 bg-primary text-white text-[8px] rounded-full">
                + 15%
              </span>
            </button>
            <button
              onClick={() => setBillingCycle("Yearly")}
              className={`px-8 py-3 rounded-full font-normal transition-all duration-200 relative ${
                billingCycle === "Yearly"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-700 hover:text-gray-900"
              }`}
            >
              Annual
              <span className="absolute -top-1 right-2 px-2 py-0.5 bg-primary text-white text-[8px] rounded-full">
                Best Value
              </span>
            </button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-gray-100 rounded-[20px] shadow-sm h-[600px] animate-pulse border border-gray-100"
                  ></div>
                ))
              : plans
                  .filter((p) => !selectedTier || p.tier === selectedTier)
                  .map((plan, index) => {
                    const isExpanded = expandedPlan === index;
                    return (
                      <div
                        key={index}
                        className={`rounded-[20px] shadow-lg overflow-hidden flex flex-col ${
                          plan.popular
                            ? "ring-2 ring-blue-600 relative bg-white"
                            : plan.isPrimary
                              ? "bg-primary text-white"
                              : "bg-white"
                        }`}
                      >
                        {plan.popular && (
                          <div className="bg-blue-600 text-white text-center py-2 text-sm font-medium">
                            Most Popular
                          </div>
                        )}

                        <div className="p-8 flex flex-col">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3
                              className={`text-2xl font-medium ${
                                plan.isPrimary ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {plan.name}
                            </h3>
                          </div>
                          <p
                            className={`text-sm font-m mb-2 ${
                              plan.isPrimary ? "text-blue-100" : "text-blue-600"
                            }`}
                          >
                            {plan.subtitle}
                          </p>
                          <p
                            className={`mb-6 ${
                              plan.isPrimary ? "text-blue-50" : "text-gray-600"
                            }`}
                          >
                            {plan.description}
                          </p>

                          <div className="mb-4">
                            {!plan.contactSales ? (
                              <>
                                <div className="flex items-baseline">
                                  <span
                                    className={`text-2xl font-normal ${
                                      plan.isPrimary
                                        ? "text-white"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    <span className="text-[14px] font-normal align-super">
                                      N
                                    </span>
                                    {billingCycle === "Yearly"
                                      ? formatPrice(plan.yearlyPrice!)
                                      : billingCycle === "Quarterly"
                                        ? formatPrice(plan.quarterlyPrice!)
                                        : formatPrice(plan.monthlyPrice!)}
                                  </span>
                                  <p
                                    className={
                                      plan.isPrimary
                                        ? "text-blue-100 text-[12px]"
                                        : "text-gray-600 text-[12px]"
                                    }
                                  >
                                    /
                                    {billingCycle === "Yearly"
                                      ? "year"
                                      : billingCycle === "Quarterly"
                                        ? "quarter"
                                        : "month"}
                                  </p>
                                </div>
                              </>
                            ) : (
                              <div className="h-[68px] flex items-center">
                                <span className="text-3xl font-medium text-white">
                                  Custom Pricing
                                </span>
                              </div>
                            )}
                          </div>

                          <div
                            className={`mb-6 text-[12px] font-medium ${
                              plan.isPrimary ? "text-blue-100" : "text-gray-500"
                            }`}
                          >
                            Setup fee:{" "}
                            <span
                              className={
                                plan.isPrimary ? "text-white" : "text-gray-900"
                              }
                            >
                              <span className="text-[9px] font-normal align-text-top">
                                {plan.isPrimary ? "" : ""}
                              </span>
                              {plan.setupFee}
                            </span>
                          </div>

                          <div
                            onClick={() => {
                              if (plan.contactSales) {
                                if (plan.isPrimary) {
                                  setShowContactModal(true);
                                } else {
                                  // Open modal for non-VIP contact sales plans
                                  setAgreementModalState({
                                    isOpen: true,
                                    planTier: plan.tier,
                                    planName: plan.name,
                                  });
                                }
                              } else {
                                if (plan.popular) {
                                  // Open modal for popular plan
                                  setAgreementModalState({
                                    isOpen: true,
                                    planTier: plan.tier,
                                    planName: plan.name,
                                  });
                                } else if (plan.isPrimary) {
                                  setShowContactModal(true);
                                } else {
                                  // Open modal for other plans
                                  setAgreementModalState({
                                    isOpen: true,
                                    planTier: plan.tier,
                                    planName: plan.name,
                                  });
                                }
                              }
                            }}
                            className={`block w-full text-center px-6 py-3 rounded-lg font-m transition-colors mb-8 mt-auto cursor-pointer ${
                              plan.isPrimary
                                ? "bg-white text-primary hover:bg-blue-50"
                                : plan.popular
                                  ? "bg-blue-600 text-white hover:bg-blue-700"
                                  : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                            }`}
                          >
                            {plan.contactSales
                              ? "Contact Sales"
                              : "Get Started"}
                          </div>

                          {!selectedTier && (
                            <div className="space-y-3 mb-8">
                              {plan.features.map((feature, featureIndex) => (
                                <div
                                  key={featureIndex}
                                  className="flex items-start space-x-3"
                                >
                                  <CheckCircle2
                                    className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                                      plan.isPrimary
                                        ? "text-blue-200"
                                        : "text-green-500"
                                    }`}
                                  />
                                  <span
                                    className={`text-sm ${
                                      plan.isPrimary
                                        ? "text-white"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    {feature}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Show total if add-ons selected */}

                          {selectedDirectAddOnIds.length > 0 &&
                            !plan.contactSales && (
                              <div
                                className={`mb-4 p-2 rounded-lg border ${
                                  plan.isPrimary
                                    ? "bg-white/10 border-white/20"
                                    : "bg-blue-50 border-blue-100"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span
                                    className={`text-sm font-m ${
                                      plan.isPrimary
                                        ? "text-blue-100"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    Total:
                                  </span>
                                  <span
                                    className={`text-sm font-normal ${
                                      plan.isPrimary
                                        ? "text-white"
                                        : "text-black"
                                    }`}
                                  >
                                    {calculateTotalPrice(plan)}
                                  </span>
                                </div>
                                {/* <div
                            className={`text-[12px] mt-1 text-right ${
                              plan.isPrimary ? "text-blue-200" : "text-gray-500"
                            }`}
                          >
                            (Base + Add-ons)
                          </div> */}
                              </div>
                            )}

                          {plan.integrationServices?.some(
                            (s: any) => s?.name,
                          ) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedPlan(isExpanded ? null : index);
                              }}
                              className={`w-full py-2.5 px-3 mb-4 border rounded-lg font-m hover:transition-all flex items-center justify-center gap-2 ${
                                plan.isPrimary
                                  ? "border-blue-400 text-white hover:bg-white/10"
                                  : "border-gray-200 text-gray-600 hover:border-blue-400 hover:bg-blue-50"
                              }`}
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUp className="w-4 h-4" />
                                  <span className="text-sm">Hide Details</span>
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4" />
                                  <span className="text-sm">View Details</span>
                                </>
                              )}
                            </button>
                          )}
                          {isExpanded &&
                            plan.integrationServices?.some(
                              (s: any) => s?.name,
                            ) && (
                              <div
                                className={`mb-6 pb-6 border-t pt-6 ${
                                  plan.isPrimary
                                    ? "border-blue-400"
                                    : "border-gray-100"
                                }`}
                              >
                                <div className="flex justify-between">
                                  <h4
                                    className={`text-sm font-bold mb-3 ${
                                      plan.isPrimary
                                        ? "text-white"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    Integration Services
                                  </h4>
                                  <span
                                    className={`text-sm font-medium mb-3 ${
                                      plan.isPrimary
                                        ? "text-white"
                                        : "text-[#212121]"
                                    }`}
                                  >
                                    Cost (NGN)
                                  </span>
                                </div>
                                <ul className="space-y-2">
                                  {plan.integrationServices
                                    ?.filter((s: any) => s && s.name)
                                    .map((service: any, i: number) => (
                                      <li
                                        key={i}
                                        className="flex items-start justify-between text-sm"
                                      >
                                        <span
                                          className={
                                            plan.isPrimary
                                              ? "text-blue-100"
                                              : "text-gray-600"
                                          }
                                        >
                                          {service.name}
                                        </span>
                                        <span
                                          className={`font-m whitespace-nowrap ml-2 ${
                                            plan.isPrimary
                                              ? "text-white"
                                              : "text-gray-600"
                                          }`}
                                        >
                                          {service.cost}
                                        </span>
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            )}

                          {/* AI Recruiter Add-on Section */}
                          <div
                            className={`mt-auto pt-6 border-t ${
                              plan.isPrimary
                                ? "border-blue-400"
                                : "border-gray-100"
                            }`}
                          >
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center justify-between">
                                {selectedAIPlan[plan.tier] && (
                                  <span className="flex items-center gap-1 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                                    <Check className="w-2.5 h-2.5" /> ADDED
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAiModalState({
                                    isOpen: true,
                                    planIndex: index,
                                  });
                                }}
                                className={`flex items-center justify-between p-3 rounded-xl border-2 border-primary transition-all group ${
                                  selectedAIPlan[plan.tier]
                                    ? "border-green-500 bg-green-50/50"
                                    : plan.isPrimary
                                      ? "border-primary hover:bg-white/10"
                                      : "border-gray-100 hover:border-blue-300 hover:bg-blue-50/30"
                                }`}
                              >
                                <div className="flex items-center gap-3 text-left">
                                  <div
                                    className={`p-2 rounded-lg ${
                                      plan.isPrimary
                                        ? "bg-white/20"
                                        : "bg-blue-100"
                                    }`}
                                  >
                                    <Zap
                                      className={`w-4 h-4 ${
                                        plan.isPrimary
                                          ? "text-white"
                                          : "text-blue-600"
                                      }`}
                                    />
                                  </div>
                                  <div>
                                    <p
                                      className={`text-sm font-m ${
                                        plan.isPrimary
                                          ? "text-white"
                                          : "text-[#616161]"
                                      }`}
                                    >
                                      AI{" "}
                                      {aiPlans[index]
                                        ? selectedAIPlan[plan.tier]
                                          ? aiPlans[index].name
                                          : `${aiPlans[index].name} Add-on`
                                        : "Add-on"}
                                    </p>
                                    <p
                                      className={`text-[10px] ${
                                        plan.isPrimary
                                          ? "text-blue-100"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      {selectedAIPlan[plan.tier]
                                        ? "Selected per tier"
                                        : ""}
                                    </p>
                                  </div>
                                </div>
                                <ChevronUp
                                  className={`w-4 h-4 rotate-90 transform transition-transform group-hover:translate-x-1 ${
                                    plan.isPrimary
                                      ? "text-white"
                                      : "text-gray-400"
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
          </div>

          <div className="mt-16 text-center">
            <p className="text-lg text-gray-600 mb-4">
              Need additional capabilities? Check out our optional add-on
              services
            </p>
            <DirectAddOns
              organizationId={id ?? undefined}
              selectedIds={selectedDirectAddOnIds}
              onToggle={handleDirectAddOnToggle}
              features={
                directAddOnFeatures.length > 0 ? directAddOnFeatures : []
              }
              showAiRecruiterAddOns={Object.keys(selectedAIPlan).length > 0}
            />
            <LifecyclePhasesSection
              phases={lifecyclePhases}
              selectedPhaseIds={selectedLifecyclePhaseIds}
              onTogglePhase={handleLifecyclePhaseToggle}
              loading={loading}
            />
          </div>
        </div>
      </section>

      {aiModalState.isOpen && aiModalState.planIndex !== null && (
        <AIPlanModal
          isOpen={aiModalState.isOpen}
          onClose={() => setAiModalState({ isOpen: false, planIndex: null })}
          plan={{
            ...aiPlans[aiModalState.planIndex || 0],
            isVip: plans[aiModalState.planIndex || 0]?.tier === "ENTERPRISE",
          }}
          onGetStarted={handleAddAIPlan}
          billingType={billingCycle}
          isSelected={
            aiModalState.planIndex !== null
              ? selectedAIPlan[plans[aiModalState.planIndex].tier] ===
                aiPlans[aiModalState.planIndex].tier
              : false
          }
          onRemove={() =>
            aiModalState.planIndex !== null &&
            handleRemoveAIPlan(plans[aiModalState.planIndex].tier)
          }
          onContactSales={() => {
            setAiModalState({ isOpen: false, planIndex: null });
            setShowContactModal(true);
          }}
        />
      )}

      <ContactSalesModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      {agreementModalState.isOpen && (
        <AgreementModal
          isOpen={agreementModalState.isOpen}
          onClose={() => setAgreementModalState({ isOpen: false })}
          onConfirm={handleAgreementConfirm}
          billingType={billingCycle}
          selectedFeatures={[]}
          selectedDirectAddOnIds={selectedDirectAddOnIds}
          link={`/agreement?Id=${id}&tier=${agreementModalState.planTier}`}
          title={agreementModalState.planName || ""}
          tier={agreementModalState.planTier || ""}
          id={id || ""}
        />
      )}
      <SelectedFeaturesFloating
        selectedItems={allSelectedItems}
        onRemove={(itemId) => {
          const numId = Number(itemId);
          if (!isNaN(numId) && selectedLifecyclePhaseIds.includes(numId)) {
            handleLifecyclePhaseToggle(numId);
          } else {
            handleDirectAddOnToggle(itemId);
          }
        }}
        totalPrice={calculateFloatingTotal()}
        billingType={billingCycle}
        title="Direct Add-ons"
      />
      {aiModalState.isOpen && aiModalState.planIndex !== null && (
        <AIPlanModal
          isOpen={aiModalState.isOpen}
          onClose={() => setAiModalState({ isOpen: false, planIndex: null })}
          plan={{
            ...(aiPlans[aiModalState.planIndex] || {}),
            isVip: plans[aiModalState.planIndex]?.tier === "ENTERPRISE",
          }}
          onGetStarted={(tier, setupFee) => {
            handleAddAIPlan(tier, setupFee);
            const directPlan = plans[aiModalState.planIndex!];
            if (directPlan && directPlan.tier !== "ENTERPRISE") {
              setAgreementModalState({
                isOpen: true,
                planTier: directPlan.tier,
                planName: directPlan.name,
              });
            } else if (directPlan) {
              setShowContactModal(true);
            }
          }}
          billingType={billingCycle}
          isSelected={
            aiModalState.planIndex !== null &&
            !!plans[aiModalState.planIndex] &&
            selectedAIPlan[plans[aiModalState.planIndex].tier] !== undefined
          }
          onRemove={() => {
            const idx = aiModalState.planIndex;
            if (idx !== null && plans[idx]) {
              handleRemoveAIPlan(plans[idx].tier);
            }
          }}
          onContactSales={() => {
            setAiModalState({ isOpen: false, planIndex: null });
            setShowContactModal(true);
          }}
        />
      )}
    </div>
  );
}
